// Imports => MOBX
import {
	observable,
	computed,
	action,
	makeObservable,
	runInAction,
	toJS,
} from 'mobx';

// Imports => Constants
import { KEYS } from '@constants';

// Imports => Utilities
import {
	AcIsSet,
	AcIsUndefined,
	AcIsNull,
	AcIsArray,
	AcAutoLoad,
	AcAutoSave,
	AcSaveState,
	AcRemoveState,
	AcClearState,
	AcFormatErrorCode,
	AcFormatErrorMessage,
} from '@utils';

const _default = {
	options: {},
	articles: [],
	article: {},
};

let app = {};

export class NewsStore {
	constructor(store) {
		makeObservable(this);

		AcAutoLoad(this, KEYS.ARTICLES);
		AcAutoSave(this);

		app.store = store;
	}

	@observable
	articles = _default.articles;

	@observable
	article = _default.article;

	@computed
	get current_articles() {
		if (
			!AcIsSet(this.articles) ||
			!AcIsArray(this.articles) ||
			this.articles.length === 0
		)
			return null;

		const sortedByPublicationDate = this.articles.slice().sort((a, b) => {
			return new Date(b.publication_date) - new Date(a.publication_date);
		});

		return toJS(sortedByPublicationDate);
	}

	@computed
	get current_number_of_articles() {
		return toJS(this.current_articles?.length);
	}

	@computed
	get has_articles() {
		return toJS(this.current_number_of_articles > 0);
	}

	@computed
	get current_recent_articles() {
		/*
			recent articles: return the (max) 3 most recent news articles
		*/
		if (!this.has_articles) return null;
		return toJS(this.current_articles.slice(0, 3));
	}

	@computed
	get current_article() {
		return toJS(this.article);
	}

	@observable
	loading = {
		status: false,
		message: null,
	};

	@action
	setLoading(state, message) {
		this.loading = {
			status: state || false,
			message: message || 'Gathering news articles',
		};
	}

	@computed
	get is_loading() {
		return toJS(this.loading.status);
	}

	@action
	index = () => {
		this.setLoading(true);

		return app.store.api.news
			.index()
			.then((response) => {
				const { data } = response;
				this.set(KEYS.ARTICLES, data);

				this.setLoading(false);

				return response;
			})
			.catch((error) => {
				app.store.toasters.add({
					variant: 'error',
					delay: 5000,
					title: `Er is iets fout gegaan tijdens het ophalen van alle nieuwsberichten`,
					description: `Probeer het opnieuw of neem contact op met <em>${KEYS.SUPPORT_EMAIL_ADDRESS}</em>`,
					code: AcFormatErrorCode(error),
				});

				this.setLoading(false);
				throw error;
			});
	};

	@action
	show = (id) => {
		this.setLoading(true);

		return app.store.api.news
			.show(id)
			.then((response) => {
				const { data } = response;
				this.set(KEYS.ARTICLE, data);

				this.setLoading(false);
				return response;
			})
			.catch((error) => {
				app.store.toasters.add({
					variant: 'error',
					delay: 5000,
					title: `Er is iets fout gegaan tijdens het ophalen van het nieuwsbericht met ID ${id}`,
					description: `Probeer het opnieuw of neem contact op met <em>${KEYS.SUPPORT_EMAIL_ADDRESS}</em>`,
					code: AcFormatErrorCode(error),
				});

				this.setLoading(false);
				throw error;
			});
	};

	@action
	set = (target, value, save = false) => {
		if (!AcIsSet(target)) return;
		if (AcIsUndefined(this[target])) return;
		if (AcIsUndefined(value)) return;

		return new Promise((resolve) => {
			this[target] = value;
			if (save) AcSaveState(target, value);
			resolve();
		});
	};

	@action
	setState = (target, property, value, save) => {
		if (!AcIsSet(target)) return;
		if (AcIsUndefined(this[target])) return;
		if (!AcIsSet(property)) return;
		if (AcIsUndefined(value)) return;

		this[target][property] = value;
		if (save) AcSaveState(target, value);
	};

	@action
	reset = (target, save = false) => {
		if (!AcIsSet(target)) return;
		if (AcIsUndefined(this[target])) return;

		return new Promise((resolve) => {
			runInAction(() => {
				this[target] = _default[target];
			});

			if (save && AcIsNull(_default[target])) {
				AcRemoveState(target);
			} else if (save) {
				AcSaveState(target, _default[target]);
			}

			resolve();
		});
	};
}

export default NewsStore;
